import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

interface Props {
  min: number | string;
  max: number | string;
  value: number;
  currentValue: number;
}

function SliderNotch({
  min: inputMin,
  max: inputMax,
  value,
  currentValue,
}: Props) {
  // At least according to TS, range inputs take a number or string for min/max. Convert to number before trying to do math:
  const min =
    typeof inputMin === 'number' ? inputMin : Number(inputMin);
  const max =
    typeof inputMax === 'number' ? inputMax : Number(inputMax);

  const percent = (value - min) / (max - min);

  // A cute thing that this component does is get lighter and higher as the slider thumb approaches. Rather than having it be a boolean when the current value is RIGHT on the notch, it transitions smoothly in the 10% of the slider closest to it.
  const range = max - min;
  const proximityThreshold = range * 0.08; // 8% of the total range
  const distance = Math.abs(currentValue - value);

  // Calculate activation intensity (0 to 1)
  // 1 when distance is 0, 0 when distance is >= proximityThreshold
  const targetActivationIntensity =
    distance <= proximityThreshold
      ? Math.max(0, 1 - distance / proximityThreshold)
      : 0;

  const spring = useSpring({
    activationIntensity: targetActivationIntensity,
    config: {
      tension: 200,
      friction: 12,
    },
  });

  return (
    <Wrapper
      style={{
        '--x': `${percent * 100}%`,
        '--activation-intensity': spring.activationIntensity,
      }}
    />
  );
}

const Wrapper = styled(animated.div)`
  position: absolute;
  left: var(--x);
  top: 0;
  bottom: 0;
  width: 3px;
  transform: translateX(-50%);

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 5px;
    width: 100%;
    background: var(--color-gray-500);
    background: color-mix(
      in srgb,
      var(--color-gray-700) calc(var(--activation-intensity) * 100%),
      var(--color-gray-300)
    );
    border-radius: 4px;
  }

  &::before {
    top: 3px;
    transform: translateY(calc(var(--activation-intensity) * -3px));
  }

  &::after {
    bottom: 3px;
    transform: translateY(calc(var(--activation-intensity) * 3px));
  }
`;

export default SliderNotch;
